import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatAmount, methodLabel, METHOD_LABELS } from "../../lib/format";
import type { ListResponse, PricingPlan } from "../../lib/types";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  SelectField,
  Sheet,
  SheetHeader,
  Skeleton,
} from "../../components/ui";

interface DraftRule {
  method: string;
  currency: string;
  model: "flat" | "percentage" | "both";
  flat: string;
  percent: string;
  min: string;
  max: string;
}

const EMPTY_RULE: DraftRule = {
  method: "",
  currency: "",
  model: "both",
  flat: "0.30",
  percent: "1.9",
  min: "",
  max: "",
};

export function Plans() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [feeBearer, setFeeBearer] = useState<"merchant" | "customer">("merchant");
  const [rules, setRules] = useState<DraftRule[]>([{ ...EMPTY_RULE }]);

  const plans = useQuery({
    queryKey: ["admin-plans"],
    queryFn: () => api.get<ListResponse<PricingPlan>>("/api/admin/pricing-plans"),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<PricingPlan>("/api/admin/pricing-plans", {
        name,
        description,
        fee_bearer: feeBearer,
        rules: rules.map((rule) => ({
          method: rule.method,
          currency: rule.currency,
          model: rule.model,
          // Amounts are entered in major units and converted here, once, so the API
          // only ever receives exact minor units.
          flat_minor: toMinor(rule.flat),
          // A percentage is entered as 1.9 and stored as 190 basis points.
          percentage_bps: Math.round(Number(rule.percent || 0) * 100),
          min_fee_minor: toMinor(rule.min),
          max_fee_minor: toMinor(rule.max),
          priority: rule.method ? 10 : 0,
        })),
      }),
    onSuccess: () => {
      setCreating(false);
      setName("");
      setDescription("");
      setRules([{ ...EMPTY_RULE }]);
      queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
    },
  });

  const rows = plans.data?.data ?? [];
  const hasFallback = rules.some((r) => !r.method && !r.currency);

  return (
    <>
      <PageHeader
        eyebrow="Platform"
        title="Pricing plans"
        description="What PayFlow charges client firms. Every plan needs a catch-all rule, so no transaction can ever be unpriceable."
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New plan
          </Button>
        }
      />

      {plans.isLoading ? (
        <Sheet>
          <Skeleton rows={6} />
        </Sheet>
      ) : rows.length === 0 ? (
        <Sheet>
          <EmptyState title="No plans" description="Create a plan to start charging client firms." />
        </Sheet>
      ) : (
        <div className="space-y-6">
          {rows.map((plan, i) => (
            <Sheet key={plan.id} className={`settle settle-${Math.min(i + 1, 5)} overflow-hidden`}>
              <SheetHeader
                title={plan.name}
                description={plan.description}
                actions={
                  <div className="flex items-center gap-2">
                    {plan.is_default && (
                      <span className="rounded-full bg-verdigris-wash px-2.5 py-0.5 text-[11px] font-medium text-verdigris">
                        Platform default
                      </span>
                    )}
                    <span className="rounded-full bg-paper-sunk px-2.5 py-0.5 text-[11px] text-ink-muted">
                      {plan.fee_bearer === "customer" ? "Customer pays" : "Merchant pays"}
                    </span>
                  </div>
                }
              />
              <ul className="divide-y divide-rule">
                {[...plan.rules]
                  .sort((a, b) => scope(b) - scope(a))
                  .map((rule) => (
                    <li key={rule.id} className="flex items-baseline justify-between gap-4 px-5 py-2.5">
                      <span className="text-[13px] text-ink">
                        {rule.method ? methodLabel(rule.method) : "All methods"}
                        {rule.currency && (
                          <span className="ml-1.5 font-mono text-[11.5px] text-ink-faint">
                            {rule.currency}
                          </span>
                        )}
                      </span>
                      <span className="text-right font-mono text-[12px] text-ink-muted">
                        {rule.model === "flat"
                          ? formatAmount(rule.flat_minor)
                          : rule.model === "percentage"
                            ? `${rule.percentage}%`
                            : `${formatAmount(rule.flat_minor)} + ${rule.percentage}%`}
                        {rule.min_fee_minor > 0 && ` · min ${formatAmount(rule.min_fee_minor)}`}
                        {rule.max_fee_minor > 0 && ` · cap ${formatAmount(rule.max_fee_minor)}`}
                      </span>
                    </li>
                  ))}
              </ul>
            </Sheet>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="New pricing plan"
        description="Rules are matched most-specific first: method and currency beats method, which beats currency, which beats the catch-all."
        footer={
          <>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!name.trim() || !hasFallback}
              onClick={() => create.mutate()}
            >
              Create plan
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <ErrorNotice error={create.error} />
          <Field label="Plan name" value={name} onChange={(e) => setName(e.target.value)} required />
          <Field
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Negotiated rate for high-volume retail"
          />
          <SelectField
            label="Who pays the fee"
            value={feeBearer}
            onChange={(e) => setFeeBearer(e.target.value as "merchant" | "customer")}
            options={[
              { value: "merchant", label: "Merchant — deducted from settlement" },
              { value: "customer", label: "Customer — added to the amount charged" },
            ]}
          />

          <div className="rule-t pt-4">
            <div className="mb-3 flex items-center justify-between">
              <p className="label">Rules</p>
              <Button size="sm" onClick={() => setRules((r) => [...r, { ...EMPTY_RULE, method: "card" }])}>
                Add rule
              </Button>
            </div>

            <div className="space-y-4">
              {rules.map((rule, index) => (
                <div key={index} className="rounded-md border border-rule p-3">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[12px] font-medium">
                      {rule.method ? methodLabel(rule.method) : "All methods"}
                      {rule.currency && ` · ${rule.currency}`}
                    </span>
                    {rules.length > 1 && (
                      <button
                        onClick={() => setRules((r) => r.filter((_, i) => i !== index))}
                        className="text-[11.5px] text-sienna underline underline-offset-2"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <SelectField
                      label="Method"
                      value={rule.method}
                      onChange={(e) => updateRule(setRules, index, { method: e.target.value })}
                      options={[
                        { value: "", label: "All methods" },
                        ...Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label })),
                      ]}
                    />
                    <SelectField
                      label="Currency"
                      value={rule.currency}
                      onChange={(e) => updateRule(setRules, index, { currency: e.target.value })}
                      options={[
                        { value: "", label: "Any currency" },
                        { value: "USD", label: "USD" },
                        { value: "ZWG", label: "ZWG" },
                      ]}
                    />
                    <SelectField
                      label="Model"
                      value={rule.model}
                      onChange={(e) =>
                        updateRule(setRules, index, { model: e.target.value as DraftRule["model"] })
                      }
                      options={[
                        { value: "both", label: "Flat + percentage" },
                        { value: "flat", label: "Flat only" },
                        { value: "percentage", label: "Percentage only" },
                      ]}
                      className="col-span-2"
                    />
                    {rule.model !== "percentage" && (
                      <Field
                        label="Flat fee"
                        value={rule.flat}
                        onChange={(e) => updateRule(setRules, index, { flat: e.target.value })}
                        placeholder="0.30"
                      />
                    )}
                    {rule.model !== "flat" && (
                      <Field
                        label="Percentage"
                        value={rule.percent}
                        onChange={(e) => updateRule(setRules, index, { percent: e.target.value })}
                        placeholder="1.9"
                        hint="e.g. 1.9 for 1.9%"
                      />
                    )}
                    <Field
                      label="Minimum fee"
                      value={rule.min}
                      onChange={(e) => updateRule(setRules, index, { min: e.target.value })}
                      placeholder="Optional"
                    />
                    <Field
                      label="Cap"
                      value={rule.max}
                      onChange={(e) => updateRule(setRules, index, { max: e.target.value })}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              ))}
            </div>

            {!hasFallback && (
              <p className="mt-3 rounded-md border border-sienna/25 bg-sienna-wash px-3 py-2 text-[12px] text-sienna">
                One rule must apply to all methods and any currency, so every transaction can be
                priced.
              </p>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

function updateRule(
  setRules: React.Dispatch<React.SetStateAction<DraftRule[]>>,
  index: number,
  patch: Partial<DraftRule>,
) {
  setRules((rules) => rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
}

/** toMinor converts a major-unit string to exact minor units, rounding once. */
function toMinor(value: string): number {
  const parsed = Number(value);
  if (!value || Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
}

function scope(rule: PricingPlan["rules"][number]): number {
  return (rule.method ? 2 : 0) + (rule.currency ? 1 : 0);
}
